import { ReactNode } from 'react'

import { styled } from '@mui/material/styles'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Snackbar, { SnackbarProps, SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'

import ErrorIcon from '@mui/icons-material/ErrorOutline'
import CheckIcon from '@mui/icons-material/CheckCircleOutline'

import { KnightColors } from 'theme'

import { SmallKnight } from 'components/Icons/SvgIcon'

const KnightAlert = styled(Alert)(({ variant, severity, theme }) => ({
  background: KnightColors.Paper,
  borderRadius: 16,
  display: 'flex',
  outline: `1px solid ${KnightColors.TextBrown}`,
  alignItems: 'center',

  '.MuiAlert-message': {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  }
}))

const anchor: SnackbarOrigin = { vertical: 'bottom', horizontal: 'center' }

interface UglySnackbarProps extends SnackbarProps {
  state: 'idle' | 'fetching' | 'error' | 'success'
}

const StateMappings = {
  idle: {
    icon: null,
    timeout: null,
  },
  fetching: {
    icon: (<CircularProgress size={32} color="inherit" />),
    timeout: null,
  },
  error: {
    icon: (<ErrorIcon fontSize="inherit" color="error" />),
    timeout: 10000,
  },
  success: {
    icon: (<CheckIcon fontSize="inherit" color="success" />),
    timeout: 1000,
  },
} as { [state: string]: { icon: ReactNode, timeout: number|null } }

export default function UglySnackbar({
  children,
  onClose,
  state,
  ...props
}: UglySnackbarProps) {
  const { icon, timeout } = StateMappings[state] ?? { icon: null, timeout: null }

  return (
    <Snackbar
      anchorOrigin={anchor}
      onClose={onClose}
      autoHideDuration={timeout}
      ClickAwayListenerProps={{ touchEvent: false, mouseEvent: false }}
      {...props}
    >
      <KnightAlert
        icon={false}
        onClose={onClose as () => void}
      >
        <Box width={32} height={32} mr={1} position="relative">
          <Box position="absolute" left={4} top={6}>
            <SmallKnight width={24} />
          </Box>
          <Box
            position="absolute"
            left={0}
            top={0}
            fontSize={32}
            color="#68310088"
          >
            { icon }
          </Box>
        </Box>
        <Typography component="p">{ children }</Typography>
      </KnightAlert>
    </Snackbar>
  )
}
